import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, IconButton, Button, CircularProgress, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useInView } from "react-intersection-observer";
import { ChatHistoryItem } from "./ChatHistoryItem";
import { getChatSessionsList, deleteChatSession, ChatSessionSummary, ChatSessionsListResponse } from "@/taskpane/services/chat";

// --- Types ---
interface ChatHistoryPanelProps {
  dossierId: string;
  sousDossierId: string;
  onClose: () => void;
  onSessionSelect: (sessionId: string) => void;
}

// --- Styled Components ---
const PanelContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "white",
  display: "flex",
  flexDirection: "column",
  zIndex: 10,
  borderRadius: "20px",
}));

const PanelHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(2),
  borderBottom: "1px solid #E4E7EC",
  flexShrink: 0,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontFamily: "Inter, sans-serif",
  fontWeight: 600,
  color: "#111827",
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  padding: 6,
  color: "#6B7280",
  "&:hover": {
    backgroundColor: "#F3F4F6",
    color: "#111827",
  },
}));

const SessionsList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  padding: theme.spacing(4),
  color: "#9CA3AF",
}));

const EmptyStateText = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontFamily: "Inter, sans-serif",
  fontWeight: 400,
  textAlign: "center",
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(2),
}));

const SkeletonItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderBottom: "1px solid #E4E7EC",
}));

// --- Component ---
export const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  dossierId,
  sousDossierId,
  onClose,
  onSessionSelect,
}) => {
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);

  // Infinite scroll trigger
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
  });

  // Initial load
  const loadSessions = useCallback(async () => {
    if (!dossierId || !sousDossierId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response: ChatSessionsListResponse = await getChatSessionsList({
        dossier_id: dossierId,
        sous_dossier_id: sousDossierId,
        limit: 5,
      });

      setSessions(response.sessions);
      setNextCursor(response.next_cursor);
      setTotalCount(response.total_count);
    } catch (err) {
      console.error("Error loading chat sessions:", err);
      setError("Impossible de charger l'historique");
    } finally {
      setIsLoading(false);
    }
  }, [dossierId, sousDossierId]);

  // Load more (pagination)
  const loadMoreSessions = useCallback(async () => {
    if (!dossierId || !sousDossierId || !nextCursor) return;

    setIsLoadingMore(true);
    setLoadMoreError(null);

    try {
      const response: ChatSessionsListResponse = await getChatSessionsList({
        dossier_id: dossierId,
        sous_dossier_id: sousDossierId,
        limit: 5,
        cursor: nextCursor,
      });

      setSessions((prev) => [...prev, ...response.sessions]);
      setNextCursor(response.next_cursor);
    } catch (err) {
      console.error("Error loading more chat sessions:", err);
      setLoadMoreError("Impossible de charger la suite.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [dossierId, sousDossierId, nextCursor]);

  // Initial load on mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Infinite scroll - load more when sentinel is in view (skip if load-more previously failed)
  useEffect(() => {
    if (inView && nextCursor && !isLoading && !isLoadingMore && !loadMoreError) {
      loadMoreSessions();
    }
  }, [inView, nextCursor, isLoading, isLoadingMore, loadMoreError, loadMoreSessions]);

  const handleSessionClick = (sessionId: string) => {
    onSessionSelect(sessionId);
  };

  // Delete session handler
  const handleDeleteSession = useCallback(async (sessionId: string) => {
    setDeletingSessionId(sessionId);
    try {
      await deleteChatSession(sessionId);
      // Remove the session from the list
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      setTotalCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error deleting chat session:", err);
      // Optionally show an error toast/message here
    } finally {
      setDeletingSessionId(null);
    }
  }, []);

  // Render loading skeletons
  const renderSkeletons = () => (
    <>
      {[1, 2, 3, 4, 5].map((i) => (
        <SkeletonItem key={i}>
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
        </SkeletonItem>
      ))}
    </>
  );

  return (
    <PanelContainer>
      <PanelHeader>
        <HeaderTitle>Historique de conversation</HeaderTitle>
        <CloseButton onClick={onClose} aria-label="Fermer l'historique">
          <CloseIcon sx={{ fontSize: "20px" }} />
        </CloseButton>
      </PanelHeader>

      <SessionsList>
        {isLoading ? (
          renderSkeletons()
        ) : error ? (
          <EmptyState>
            <EmptyStateText>{error}</EmptyStateText>
            <Button
              variant="outlined"
              size="small"
              onClick={loadSessions}
              sx={{ mt: 2 }}
            >
              Réessayer
            </Button>
          </EmptyState>
        ) : sessions.length === 0 ? (
          <EmptyState>
            <EmptyStateText>Aucune conversation précédente</EmptyStateText>
            <EmptyStateText sx={{ mt: 1, fontSize: "13px" }}>Vos questions et réponses apparaîtront ici</EmptyStateText>
          </EmptyState>
        ) : (
          <>
            {sessions.map((session) => (
              <ChatHistoryItem
                key={session.session_id}
                session={session}
                onSessionClick={handleSessionClick}
                onDeleteSession={handleDeleteSession}
                isDeleting={deletingSessionId === session.session_id}
              />
            ))}

            {/* Infinite scroll sentinel */}
            {nextCursor && (
              <LoadingContainer ref={loadMoreRef}>
                {isLoadingMore && <CircularProgress size={24} />}
                {loadMoreError && !isLoadingMore && (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>{loadMoreError}</Typography>
                    <Button variant="outlined" size="small" onClick={loadMoreSessions}>
                      Réessayer
                    </Button>
                  </Box>
                )}
              </LoadingContainer>
            )}

            {/* Show total count */}
            {!nextCursor && sessions.length > 0 && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#9CA3AF",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {totalCount} conversation{totalCount > 1 ? "s" : ""} au total
                </Typography>
              </Box>
            )}
          </>
        )}
      </SessionsList>
    </PanelContainer>
  );
};

export default ChatHistoryPanel;
